import { GetStaticProps } from 'next';
import {
  fetchCommunityRemixes,
  fetchCommunityShot,
} from 'lib/api/community_api';
import { getCommunityIdsFromSlug } from 'lib/community';
import CommunityShot from 'components/community/shot/CommunityShot';
import ShotMetatags from 'components/community/shot/ShotMetatags';
import { CommunityShotItem, ShotRemix } from 'types/community';
import LayoutV2 from 'components/common/LayoutV2';
import CommunityShotContainer from 'components/community/shot/CommunityShotContainer';
import Head from 'next/head';

type Props = {
  shotItem: CommunityShotItem;
  remixes: ShotRemix[];
};

export default function CommunityShotPage({ shotItem, remixes }: Props) {
  return (
    <>
      <Head>
        <link
          rel="preload"
          href={shotItem.fileUrl ?? undefined}
          as="fetch"
          crossOrigin="anonymous"
        />
      </Head>
      <LayoutV2 title={'Community Shot'} navTitle="Marketplace">
        {shotItem && <ShotMetatags shotItem={shotItem} />}

        <CommunityShotContainer>
          <CommunityShot
            shotItem={shotItem}
            remixes={remixes}
            key={shotItem.communityRevisionId.toString()}
          />
        </CommunityShotContainer>
      </LayoutV2>
    </>
  );
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { communityPostId, communityRevisionId } = getCommunityIdsFromSlug(
    params?.id as string
  );

  // If what we've parsed is junk, just return not found to the user.
  if (communityPostId === undefined || isNaN(communityPostId)) {
    return {
      notFound: true,
    };
  }

  const remixesPromise = fetchCommunityRemixes(communityPostId);
  const shotItemPromise = fetchCommunityShot(
    communityPostId,
    communityRevisionId
  );

  const remixes = await remixesPromise;
  const shotItem = await shotItemPromise;

  if (shotItem === null) {
    return {
      notFound: true,
      revalidate: 1,
    };
  }

  return {
    props: {
      shotItem,
      remixes,
    },
    revalidate: 1,
  };
};
